<template>
    <div class="part-toolbar d-flex justify-content-between">
        <div class="part-toolbar-main d-flex justify-content-between mt-4">
            <div class="multi-part-selector d-flex justify-content-between">
                <div class="part-counter d-flex align-items-center me-2" style="height: 50px">
                    <div style="width: 60px">
                        <h3>{{ selectedIndex + 1 }} of {{ parts.length }}</h3>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn"
                    :class="{
                        'btn-danger': displayPrevButton,
                        'btn-secondary': !displayPrevButton,
                    }"
                    :disabled="!displayPrevButton"
                    @click="prevPart"
                >
                    <font-awesome-icon :icon="['fas', 'chevron-left']" />
                </button>
                <select
                    class="form-select form-select-lg mb-3 mx-2"
                    aria-label="Selected part number"
                    v-model.number="selectedIndex"
                >
                    <option v-for="(part, index) in parts" :value="index" :key="index">
                        {{ part }}
                    </option>
                </select>
                <button
                    type="button"
                    class="btn"
                    :class="{
                        'btn-danger': displayNextButton,
                        'btn-secondary': !displayNextButton,
                    }"
                    :disabled="!displayNextButton"
                    @click="nextPart"
                >
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </button>
            </div>
            <slot name="status">
                <div class="mx-2 d-flex justify-content-between" style="height: 40px" v-if="loading">
                    <div class="spinner-border text-danger m-auto" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
                <div class="d-flex align-items-center" style="height: 40px" v-if="loading">
                    {{ loadingMessage }}
                </div>
            </slot>
        </div>
        <div class="part-toolbar-actions d-flex align-items-center">
            <div class="d-flex align-items-center me-2">
                <button type="button" class="btn btn-danger" @click="openSubstitutesModal">
                    Substitutes
                </button>
            </div>
            <div v-if="allowAddNote" class="d-flex align-items-center me-2">
                <button
                    type="button"
                    class="btn btn-danger"
                    @click="addNote"
                >
                    Add Note
                </button>
            </div>
            <div class="d-flex align-items-center">
                <button type="button" class="btn btn-danger" @click="resetSearch">
                    Reset Search
                </button>
            </div>
        </div>
    </div>
    <SubstituteModal
        ref="subModal"
        :partLabel="currentPart"
        @select-substitute="onSubSelected"
        @opened="onSubModalOpened"
        @closed="onSubModalClosed"
    />
</template>

<script>
    import SubstituteModal from '../modals/SubstituteModal.vue'
    import { mapStores } from 'pinia'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        components: { SubstituteModal },
        name: 'MultiPartSelector',
        data() {
            return {
                selectedIndex: 0,
                isSubstitutesModalOpen: false,
            }
        },
        props: {
            selectedPart: {
                type: String,
                required: true,
            },
            parts: {
                type: Array,
                required: true,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            loadingMessage: {
                type: String,
                default: 'Loading...',
            },
            allowAddNote: {
                type: Boolean,
                default: true,
            },
        },
        computed: {
            ...mapStores(useSubstitutesStore),
            substitutesLoading() {
                return this.substitutesStore.loading
            },
            substitutesError() {
                return this.substitutesStore.error
            },
            partIndexFromStore() {
                return this.searchStore.search.selectedPartIndex
            },
            displayPrevButton() {
                return this.selectedIndex > 0
            },
            displayNextButton() {
                return this.selectedIndex < this.parts.length - 1
            },
            currentPart() {
                return this.parts[this.selectedIndex] || ''
            },
        },
        emits: ['setPartSelected', 'reset-search', 'add-note'],
        mounted() {
            // initialize selectedIndex using store > prop > 0
            const idxFromStore =
                typeof this.partIndexFromStore === 'number' ? this.partIndexFromStore : -1
            const idxFromProp = this.parts.findIndex((p) => p === this.selectedPart)

            if (idxFromStore >= 0 && idxFromStore < this.parts.length) {
                this.selectedIndex = idxFromStore
            } else if (idxFromProp >= 0) {
                this.selectedIndex = idxFromProp
            } else {
                this.selectedIndex = 0
            }

            // emit once so parent is synced
            this.$emit('setPartSelected', { part: this.currentPart, index: this.selectedIndex })
        },
        watch: {
            // external index (store) → sync local
            partIndexFromStore(newValue) {
                if (typeof newValue === 'number' && newValue !== this.selectedIndex) {
                    this.selectedIndex = this.clampIndex(newValue)
                }
            },
            // when user changes selection → inform parent and refresh modal content
            selectedIndex() {
                this.$emit('setPartSelected', { part: this.currentPart, index: this.selectedIndex })
                this.loadSubstitutesFromStore([this.currentPart])
                if (this.isSubstitutesModalOpen) {
                    this.refreshSubstitutesModal()
                }
            },
            // when list of parts changes → keep index valid and (re)load substitutes once
            parts: {
                handler(newParts) {
                    if (!Array.isArray(newParts) || newParts.length === 0) {
                        this.selectedIndex = 0
                        return
                    }
                    if (this.selectedIndex >= newParts.length) {
                        this.selectedIndex = 0
                    }
                    this.loadSubstitutesFromStore(newParts)
                },
                immediate: true,
            },
        },
        methods: {
            clampIndex(i) {
                if (!this.parts.length) return 0
                return Math.min(Math.max(i, 0), this.parts.length - 1)
            },
            prevPart() {
                if (this.selectedIndex > 0) {
                    this.selectedIndex -= 1
                }
            },
            nextPart() {
                if (this.selectedIndex < this.parts.length - 1) {
                    this.selectedIndex += 1
                }
            },
            resetSearch() {
                this.$emit('reset-search')
            },
            addNote() {
                this.$emit('add-note')
            },
            openSubstitutesModal() {
                const code = this.currentPart
                if (!code) return

                this.loadSubstitutesFromStore([code])
                this.$refs.subModal.open(code)
            },
            onSubModalOpened() {
                this.isSubstitutesModalOpen = true
                this.refreshSubstitutesModal()
            },
            onSubModalClosed() {
                this.isSubstitutesModalOpen = false
            },
            refreshSubstitutesModal() {
                if (!this.isSubstitutesModalOpen || !this.$refs.subModal) return
                const code = this.currentPart
                this.$refs.subModal.sync(code)
            },
            onSubSelected(item) {
                console.log('Subsitute selected ' + item.name)
                // Optionally switch selection:
                // const idx = this.parts.findIndex(p => p === item.name)
                // if (idx >= 0) this.selectedIndex = idx
            },
            loadSubstitutesFromStore(partsList = []) {
                const normalizedParts = (partsList || [])
                    .filter((part) => part != null)
                    .map((part) => part.toString().trim())
                    .filter((part) => part.length > 0)

                if (!normalizedParts.length) return

                this.substitutesStore.fetchSubstitutes(normalizedParts)
            },
        },
    }
</script>

<style scoped>
    .multi-part-selector-box {
        width: 400px;
    }

    .multi-part-selector button,
    .multi-part-selector select {
        height: 48px;
    }
    .Toastify {
        z-index: 11111111;
    }
</style>
